import { useState } from 'react';
import { supabase, signUp, getLearner, updateLearnerProfile } from '../services/supabaseClient.js';
import { track } from '../utils/track.js';

// Single-screen signup. No payment step — free lessons start immediately and
// billing happens later at the paywall (after the free lessons are used).
//
//   Full name + email + password + phone (OPTIONAL), plus Terms / Privacy / SMS
//   consent. On submit: supabase.auth.signUp() creates the auth user; the
//   signUp() helper inserts the learners row (free_lesson_limit defaults to 3);
//   we then write phone +
//   sms_consent + age_confirmation and hand off to the dashboard. No Stripe call
//   here.
//
//   Signup does NOT stamp onboarding_completed_at — that flag now means
//   "personalized" and is stamped by the Roadmap personalization flow, not at
//   signup. A freshly-signed-up learner lands on the dashboard with the
//   "Personalize your path" nudge active.
//
// If a user reaches this screen already authenticated (e.g. the /signin?signup=1
// admin bypass), the email is pre-filled read-only and the password field is
// hidden; submit just updates the existing learners row.

export default function OnboardingScreen({ learner, onComplete, theme, toggleTheme }) {
  // Three entry states this screen handles:
  //   1. No learner (true new-user signup): Screen 1 with all fields, calls signUp().
  //   2. Learner exists but profile incomplete (came from AuthScreen.signup with
  //      name=null): Screen 1 with email pre-filled (read-only), password hidden;
  //      submit just updates the learners row.
  //   3. Learner exists with name already filled: skip to Screen 2.
  const alreadySignedUp = !!learner?.id;
  const profileComplete = alreadySignedUp && !!learner?.name;
  // Single-screen onboarding now: account + profile, then straight to the
  // dashboard. No payment step — billing happens at the paywall after the
  // free lessons are used. (profileComplete retained for the read-only email
  // / hidden-password branch when a learner arrives already signed up.)
  const [screen] = useState(1);

  // Screen 1 state ----------------------------------------------------
  // If the user is already signed up, pre-fill email and skip password — the
  // submit path will update the existing learners row instead of calling signUp.
  const [fullName, setFullName]     = useState(learner?.name || '');
  const [email, setEmail]           = useState(learner?.email || '');
  const [password, setPassword]     = useState('');
  const [showPassword, setShowPwd]  = useState(false);
  const [phone, setPhone]           = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [showSmsTerms, setShowSmsTerms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  // Screen 2 — the learner row we'll be writing to. If we started at Screen 2
  // because a learner already exists, use that; otherwise the new learner is
  // assigned after Screen 1 succeeds.
  const [currentLearner, setCurrentLearner] = useState(learner || null);

  // ── Screen 1 submit: create the auth user + write profile ─────────
  const submitScreen1 = async () => {
    setError('');
    if (!fullName.trim())   { setError('Please enter your full name.'); return; }
    if (!alreadySignedUp) {
      if (!email.trim())      { setError('Email is required.'); return; }
      if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    }
    if (!agreeTerms || !agreePrivacy) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setSubmitting(true);
    try {
      // Split full_name → first_name + last_name (last token = last_name,
      // everything before = first_name). Single-word names leave last_name empty.
      const parts = fullName.trim().split(/\s+/);
      const lastName  = parts.length > 1 ? parts[parts.length - 1] : '';
      const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0];

      let userId;
      if (alreadySignedUp) {
        // User came from AuthScreen.signup; just update the existing learner row.
        userId = learner.id;
      } else {
        // signUp() inserts a learners row with { id, email, name } and a streaks row.
        const user = await signUp(email.trim(), password, fullName.trim());
        userId = user.id;
        track('signup', { method: 'email' });
        // Affiliate attribution: if the visitor arrived via /?ref=CODE, persist
        // the code on the learner row so referral_attributed can fire on the
        // paid-conversion webhook. Fire-and-forget — never block onboarding.
        const ref = localStorage.getItem('ref');
        if (ref) {
          supabase.from('learners').update({ referred_by: ref }).eq('id', userId)
            .then(() => {}, () => {});
        }
      }

      const nowIso = new Date().toISOString();
      await updateLearnerProfile(userId, {
        name:             fullName.trim(),
        first_name:       firstName,
        last_name:        lastName,
        phone:            phone.trim() || null,   // phone is optional now
        sms_consent:      smsConsent,
        sms_consent_at:   smsConsent ? nowIso : null,
        age_confirmation: true,
      });

      // No payment step — account creation ends here. Hand off to the dashboard;
      // personalization (and the onboarding_completed_at stamp) happens later
      // from the Roadmap. Billing happens at the paywall after free lessons.
      await finishOnboarding(userId, { email: email.trim() || learner?.email, created_at: nowIso });
    } catch (err) {
      setError(err?.message || 'Could not create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Hand off to the dashboard ───────────────────────────────────
  // Signup no longer stamps onboarding_completed_at (that now means
  // "personalized" and is set by the Roadmap flow). Free lessons start
  // immediately; billing happens at the paywall once they're used.
  const finishOnboarding = async (learnerId, extra = {}) => {
    const id = learnerId || currentLearner?.id;
    if (!id) return;
    track('signup_completed');
    const fresh = await getLearner(id);
    onComplete?.({
      id,
      ...(currentLearner || {}),
      ...extra,
      ...(fresh || {}),
    });
  };

  // ── Style tokens (CSS variables with dark-theme fallbacks) ────────
  const styles = {
    page: {
      minHeight: '100vh',
      background: 'var(--bg-primary, #0f0f12)',
      color: 'var(--text-primary, #e8e6e1)',
      fontFamily: "'Hanken Grotesk', 'Inter', system-ui, sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 16px',
    },
    card: {
      width: '100%',
      maxWidth: 480,
      background: 'var(--bg-card, #16161c)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      borderRadius: 20,
      overflow: 'hidden',
      padding: 'clamp(28px, 4vw, 44px) clamp(22px, 4vw, 40px)',
      boxShadow: 'var(--shadow-card, 0 12px 40px rgba(0,0,0,0.5))',
    },
    logoWrap: {
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      background: '#1a1a24',
      // Full-bleed: negate the card padding so the banner spans the full card
      // width and reaches the very top (card overflow:hidden clips the corners).
      margin: 'calc(-1 * clamp(28px, 4vw, 44px)) calc(-1 * clamp(22px, 4vw, 40px)) 26px',
      padding: '26px 20px',
    },
    logo:    { height: 150, width: 'auto', objectFit: 'contain' },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: '#d97706',
      textAlign: 'center',
      marginBottom: 10,
    },
    headline: {
      fontFamily: "'Fraunces', serif",
      fontSize: 'clamp(22px, 3vw, 28px)',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      textAlign: 'center',
      margin: '0 0 24px',
      color: 'var(--text-primary, #e8e6e1)',
    },
    fieldGroup: { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 },
    field:      { display: 'flex', flexDirection: 'column', gap: 6 },
    label: {
      fontSize: 11,
      fontWeight: 700,
      color: 'var(--text-muted, #8f8b82)',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    input: {
      padding: '12px 14px',
      borderRadius: 10,
      background: 'var(--bg-elevated, #1c1c24)',
      border: '1px solid var(--border-medium, rgba(255,255,255,0.10))',
      color: 'var(--text-primary, #e8e6e1)',
      fontSize: 14,
      outline: 'none',
      fontFamily: 'inherit',
      width: '100%',
      boxSizing: 'border-box',
    },
    inputWithToggle: { position: 'relative' },
    pwToggle: {
      position: 'absolute',
      right: 12,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'transparent',
      border: 'none',
      color: 'var(--text-muted, #8f8b82)',
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      cursor: 'pointer',
    },
    consentBlock: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 },
    consentRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      fontSize: 13,
      lineHeight: 1.5,
      color: 'var(--text-secondary, #a8a49e)',
      cursor: 'pointer',
    },
    consentCheckbox: { marginTop: 3, cursor: 'pointer' },
    consentLink: {
      color: 'var(--accent-primary, #2dd4bf)',
      textDecoration: 'underline',
    },
    linkBtn: {
      background: 'none', border: 'none', padding: 0, font: 'inherit',
      color: 'var(--accent-primary, #2dd4bf)', textDecoration: 'underline', cursor: 'pointer',
    },
    smsOverlay: {
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    smsModal: {
      width: '100%', maxWidth: 380, background: 'var(--bg-card, #ffffff)',
      border: '1px solid var(--border-soft, rgba(0,0,0,0.08))', borderRadius: 16,
      padding: '24px 22px', boxShadow: '0 24px 60px rgba(15,23,42,0.28)',
    },
    smsTitle: { fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600, color: 'var(--text-primary, #111110)', margin: '0 0 10px' },
    smsBody:  { fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-secondary, #3a3835)', margin: 0 },
    smsClose: {
      width: '100%', marginTop: 18, padding: '11px 20px', borderRadius: 10, border: 'none',
      background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#ffffff',
      fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
    },
    primaryBtn: {
      width: '100%',
      padding: '13px 24px',
      borderRadius: 12,
      background: 'linear-gradient(135deg, var(--accent-hover, #14b8a6), var(--accent-primary, #2dd4bf))',
      border: 'none',
      color: 'var(--accent-text, #0f0f12)',
      fontSize: 15,
      fontWeight: 700,
      cursor: 'pointer',
      fontFamily: 'inherit',
      letterSpacing: 0.2,
      transition: 'opacity 0.2s, transform 0.2s',
    },
    primaryBtnDisabled: {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
    skipBtn: {
      width: '100%',
      padding: '13px 24px',
      borderRadius: 12,
      // Soft accent tint — sits between the muted ghost button and the
      // gradient primary CTA. Clearly the alternative path, but still
      // visually inviting (not "do you really want to skip?" hostile).
      background: 'var(--accent-soft, rgba(45,212,191,0.12))',
      border: '1px solid var(--accent-primary, #2dd4bf)',
      color: 'var(--accent-primary, #2dd4bf)',
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: 0.2,
      cursor: 'pointer',
      fontFamily: 'inherit',
      transition: 'background 0.2s, transform 0.2s',
    },
    errorBox: {
      padding: '10px 14px',
      borderRadius: 8,
      background: 'rgba(248,113,113,0.10)',
      border: '1px solid rgba(248,113,113,0.25)',
      color: 'var(--progress-weak, #f87171)',
      fontSize: 13,
      lineHeight: 1.5,
      marginBottom: 16,
    },

    // Screen 2 specific
    incentiveCard: {
      background: 'var(--bg-elevated, #1c1c24)',
      border: '1px solid var(--accent-primary, #2dd4bf)',
      borderRadius: 14,
      padding: '20px 22px',
      marginBottom: 16,
    },
    incentivePrice: {
      fontFamily: "'Fraunces', serif",
      fontSize: 'clamp(28px, 4vw, 36px)',
      fontWeight: 500,
      color: 'var(--accent-primary, #2dd4bf)',
      margin: '4px 0 6px',
    },
    incentiveBody: {
      fontSize: 14,
      lineHeight: 1.65,
      color: 'var(--text-secondary, #a8a49e)',
      margin: 0,
    },
    secureRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      marginTop: 12,
      fontSize: 12,
      color: 'var(--text-muted, #8f8b82)',
    },
    microcopy: {
      fontSize: 12,
      color: 'var(--text-muted, #8f8b82)',
      textAlign: 'center',
      marginTop: 10,
    },

    // Divider between the "pay" path and the "skip" path. Centered "or"
    // label flanked by hairline rules.
    divider: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      margin: '24px 0 16px',
    },
    dividerLine: {
      flex: 1,
      height: 1,
      background: 'var(--border-medium, rgba(255,255,255,0.10))',
    },
    dividerLabel: {
      flexShrink: 0,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--text-muted, #8f8b82)',
    },

    // Skip explainer — sits ABOVE the skip button so the learner reads the
    // consequences before clicking. Bullet list, muted color, no border.
    skipExplainer: {
      background: 'var(--bg-elevated, #1c1c24)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      borderRadius: 10,
      padding: '14px 16px',
      marginBottom: 12,
    },
    skipExplainerHead: {
      fontSize: 13,
      fontWeight: 700,
      color: 'var(--text-primary, #e8e6e1)',
      margin: '0 0 8px',
    },
    skipExplainerList: {
      margin: 0,
      paddingLeft: 18,
      fontSize: 13,
      lineHeight: 1.65,
      color: 'var(--text-secondary, #a8a49e)',
    },
  };

  // Required-fields check for Screen 1 submit button. For already-signed-up
  // users, skip email/password checks (those fields aren't shown).
  const screen1Valid = fullName.trim()
    && (alreadySignedUp || (email.trim() && password.length >= 8))
    && agreeTerms
    && agreePrivacy;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <img src="/logo.jpg" alt="AdaptOS" style={styles.logo} />
        </div>

        {screen === 1 && (
          <>
            <div style={styles.eyebrow}>Start free — 3 lessons, no card required</div>
            <h1 style={styles.headline}>Create your AdaptOS account</h1>

            <div style={styles.fieldGroup}>
              <div style={styles.field}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  placeholder="Your name"
                  onChange={e => setFullName(e.target.value)}
                  style={styles.input}
                  autoComplete="name"
                />
              </div>

              {!alreadySignedUp && (
                <>
                  <div style={styles.field}>
                    <label style={styles.label}>Email</label>
                    <input
                      type="email"
                      value={email}
                      placeholder="you@example.com"
                      onChange={e => setEmail(e.target.value)}
                      style={styles.input}
                      autoComplete="email"
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>Password</label>
                    <div style={styles.inputWithToggle}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        placeholder="At least 8 characters"
                        onChange={e => setPassword(e.target.value)}
                        style={{ ...styles.input, paddingRight: 64 }}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd(s => !s)}
                        style={styles.pwToggle}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div style={styles.field}>
                <label style={styles.label}>Phone (optional — for SMS reminders)</label>
                <input
                  type="tel"
                  value={phone}
                  placeholder="(555) 555-5555"
                  onChange={e => setPhone(e.target.value)}
                  style={styles.input}
                  autoComplete="tel"
                />
              </div>
            </div>

            <div style={styles.consentBlock}>
              <label style={styles.consentRow}>
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={e => setAgreeTerms(e.target.checked)}
                  style={styles.consentCheckbox}
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>
                    Terms of Service
                  </a>.
                </span>
              </label>

              <label style={styles.consentRow}>
                <input
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={e => setAgreePrivacy(e.target.checked)}
                  style={styles.consentCheckbox}
                />
                <span>
                  I agree to the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.consentLink}>
                    Privacy Policy
                  </a>.
                </span>
              </label>

              <label style={styles.consentRow}>
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={e => setSmsConsent(e.target.checked)}
                  style={styles.consentCheckbox}
                />
                <span>
                  I&rsquo;d like to receive SMS study reminders{' '}
                  <button type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowSmsTerms(true); }}
                    style={styles.linkBtn}>
                    (details)
                  </button>. Optional.
                </span>
              </label>
            </div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <button
              onClick={submitScreen1}
              disabled={submitting || !screen1Valid}
              style={{
                ...styles.primaryBtn,
                ...((submitting || !screen1Valid) ? styles.primaryBtnDisabled : {}),
              }}
            >
              {submitting ? 'Creating account…' : 'Start Free Lessons →'}
            </button>

            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted, #8f8b82)', marginTop: 18 }}>
              Already have an account?{' '}
              <a href="/signin" style={styles.consentLink}>Sign in</a>
            </p>
          </>
        )}
      </div>

      {/* SMS consent — frictionless popup; simple opt-in terms, no page nav. */}
      {showSmsTerms && (
        <div role="dialog" aria-modal="true" style={styles.smsOverlay} onClick={() => setShowSmsTerms(false)}>
          <div style={styles.smsModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.smsTitle}>SMS study reminders</h3>
            <p style={styles.smsBody}>
              Opt in to get occasional automated study-reminder texts from AdaptOS at the
              number you provide. It&rsquo;s optional and never required to sign up or pay.
              Message frequency varies and message &amp; data rates may apply. Reply
              <strong> STOP</strong> to opt out or <strong>HELP</strong> for help at any time.
            </p>
            <button type="button" style={styles.smsClose} onClick={() => setShowSmsTerms(false)}>Got it</button>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..600&family=Hanken+Grotesk:wght@300;400;500;600;700&display=swap');
        a:hover { opacity: 0.85; }
        button:not(:disabled):hover { transform: translateY(-1px); }
      `}</style>
    </div>
  );
}
